import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  HStack,
  IconButton,
  Tooltip,
  useCustomToast,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useDeleteDedDetailApi } from 'apis/crm-ded-details';
import React from 'react';
import { FiEdit3, FiEye, FiTrash2 } from 'react-icons/fi';

interface ActionsCellProps {
  detailId: number;
  businessName: string;
  onView: (detailId: number) => void;
  onEdit: (detailId: number) => void;
}

export const ActionsCell: React.FC<ActionsCellProps> = ({ detailId, businessName, onView, onEdit }) => {
  const toast = useCustomToast();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef(null);

  const { mutate: deleteDetail, isLoading: isDeleting } = useDeleteDedDetailApi({
    onSuccess: () => {
      toast({
        title: 'DED Detail Deleted',
        description: `${businessName} has been successfully deleted.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onDeleteClose();
    },
    onError: (error: any) => {
      toast({
        title: 'Delete Failed',
        description: error?.response?.data?.message || 'Failed to delete DED detail. Please try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const handleDelete = () => {
    deleteDetail(detailId);
  };

  const handleView = () => {
    onView(detailId);
    toast({
      title: 'Opening Details View',
      description: `Loading details for ${businessName}...`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleEdit = () => {
    onEdit(detailId);
    toast({
      title: 'Opening Edit Mode',
      description: `Loading edit form for ${businessName}...`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  return (
    <>
      <div
        style={{
          height: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px 0',
        }}>
        <HStack spacing={2}>
          <Tooltip label={`View ${businessName} details`}>
            <IconButton
              aria-label={`View ${businessName} details`}
              icon={<FiEye />}
              size='sm'
              colorScheme='blue'
              variant='outline'
              onClick={handleView}
            />
          </Tooltip>

          <Tooltip label={`Edit ${businessName}`}>
            <IconButton
              aria-label={`Edit ${businessName}`}
              icon={<FiEdit3 />}
              size='sm'
              colorScheme='green'
              variant='outline'
              onClick={handleEdit}
            />
          </Tooltip>

          <Tooltip label={`Delete ${businessName}`}>
            <IconButton
              aria-label={`Delete ${businessName}`}
              icon={<FiTrash2 />}
              size='sm'
              colorScheme='red'
              variant='outline'
              onClick={onDeleteOpen}
            />
          </Tooltip>
        </HStack>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              Delete DED Detail
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete <strong>{businessName}</strong>? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme='red' onClick={handleDelete} ml={3} isLoading={isDeleting} loadingText='Deleting...'>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
